import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Knowledge Bases API using RTK Query
 *
 * Provides dedicated knowledge base endpoints with automatic caching,
 * request deduplication, and loading state management.
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const basesApi = createApi({
  reducerPath: 'basesApi',
  baseQuery,
  tagTypes: ['KnowledgeBase'],
  // Cache configuration
  keepUnusedDataFor: 300, // 5 minutes cache
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get list of dedicated knowledge bases
     */
    getBases: builder.query({
      query: () => '/api/v1/segmented-knowledge/',
      // LIST/ITEM pattern: provide both individual item tags and list tag
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'KnowledgeBase', id })),
              { type: 'KnowledgeBase', id: 'LIST' },
            ]
          : [{ type: 'KnowledgeBase', id: 'LIST' }],
    }),

    /**
     * Get single dedicated knowledge base by ID
     */
    getBaseById: builder.query({
      query: (id) => `/api/v1/segmented-knowledge/${id}/`,
      providesTags: (result, error, id) => [{ type: 'KnowledgeBase', id }],
    }),

    /**
     * Create new dedicated knowledge base
     */
    createBase: builder.mutation({
      query: (baseData) => ({
        url: '/api/v1/segmented-knowledge/',
        method: 'POST',
        body: baseData,
      }),
      // Only invalidate the LIST tag
      invalidatesTags: [{ type: 'KnowledgeBase', id: 'LIST' }],
    }),

    /**
     * Update existing dedicated knowledge base
     */
    updateBase: builder.mutation({
      query: ({ id, ...baseData }) => ({
        url: `/api/v1/segmented-knowledge/${id}/`,
        method: 'PUT',
        body: baseData,
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, { id }) => [
        { type: 'KnowledgeBase', id },
        { type: 'KnowledgeBase', id: 'LIST' },
      ],
    }),

    /**
     * Delete dedicated knowledge base
     */
    deleteBase: builder.mutation({
      query: (id) => ({
        url: `/api/v1/segmented-knowledge/${id}/`,
        method: 'DELETE',
      }),
      // Invalidate both the specific item and the list
      invalidatesTags: (result, error, id) => [
        { type: 'KnowledgeBase', id },
        { type: 'KnowledgeBase', id: 'LIST' },
      ],
    }),

    /**
     * Get available segmentation codes from employees
     * Returns company codes and collective agreement codes
     */
    getSegmentationCodes: builder.query({
      query: () => '/api/v1/employees/segmentation-codes/',
      keepUnusedDataFor: 600, // 10 minutes cache (codes don't change frequently)
    }),
  }),
});

export const {
  useGetBasesQuery,
  useGetBaseByIdQuery,
  useCreateBaseMutation,
  useUpdateBaseMutation,
  useDeleteBaseMutation,
  useGetSegmentationCodesQuery,
} = basesApi;
